import {
  BitkitIconComponent,
  IconClaudeColor,
  IconCopilot,
  IconCursor,
  IconGlobe,
  IconGoogleColor,
  IconKiroColor,
  IconVsCode,
  IconWindsurf,
} from '@bitrise/bitkit-v2';

import { userSecurityPagePath } from '@/utils/monolithApiRouteService';

const MCP_SERVER_URL = 'https://mcp.bitrise.io';

const REMOTE_ENTRY = `{
  "mcpServers": {
    "bitrise": {
      "url": "${MCP_SERVER_URL}"
    }
  }
}`;

const REMOTE_ENTRY_WITH_TOKEN = `{
  "mcpServers": {
    "bitrise": {
      "url": "${MCP_SERVER_URL}",
      "headers": {
        "Authorization": "Bearer <token>"
      }
    }
  }
}`;

const COPILOT_ENTRY = `{
  "servers": {
    "bitrise": {
      "url": "${MCP_SERVER_URL}"
    }
  }
}`;

const VS_CODE_ENTRY = `{
  "servers": {
    "bitrise": {
      "type": "http",
      "url": "${MCP_SERVER_URL}"
    }
  }
}`;

const WINDSURF_ENTRY = `{
  "mcpServers": {
    "bitrise": {
      "serverUrl": "${MCP_SERVER_URL}"
    }
  }
}`;

const MCP_REMOTE_ENTRY = `{
  "mcpServers": {
    "bitrise": {
      "command": "npx",
      "args": ["mcp-remote", "${MCP_SERVER_URL}"]
    }
  }
}`;

const KIRO_OAUTH_ENTRY = `{
  "mcpServers": {
    "bitrise": {
      "type": "http",
      "url": "${MCP_SERVER_URL}"
    }
  }
}`;

const vsCodeDeeplink = `vscode:mcp/install?${encodeURIComponent(
  JSON.stringify({ name: 'bitrise', type: 'http', url: MCP_SERVER_URL }),
)}`;

const cursorDeeplink = `cursor://anysphere.cursor-deeplink/mcp/install?name=bitrise&config=${btoa(
  JSON.stringify({ url: MCP_SERVER_URL }),
)}`;

const createTokenStep = {
  text: 'Create a token in ',
  link: { label: 'Account Settings → Security', href: userSecurityPagePath() },
};

export type McpClientSnippet = {
  code: string;
  variant: 'single' | 'multi';
};

export type McpClientStep = {
  text: string;
  link?: { label: string; href: string };
  snippet?: McpClientSnippet;
};

type McpClientFallback = {
  title: string;
  text: string;
  snippet?: McpClientSnippet;
};

export type McpClientId =
  'claude_code' | 'claude_desktop' | 'vs_code' | 'cursor' | 'copilot' | 'windsurf' | 'gemini_cli' | 'kiro' | 'other';

export type McpClient = {
  id: McpClientId;
  name: string;
  icon: BitkitIconComponent;
  label?: string;
  deeplink?: { label: string; href: string; note: string };
  steps?: McpClientStep[];
  snippet?: McpClientSnippet;
  note?: string;
  fallback?: McpClientFallback;
};

const TOKEN_FALLBACK: McpClientFallback = {
  title: "On an older version that can't sign in?",
  text: 'Use a personal access token instead: add the same entry with an extra header.',
  snippet: { code: '"headers": { "Authorization": "Bearer <token>" }', variant: 'single' },
};

const MCP_REMOTE_FALLBACK: McpClientFallback = {
  title: 'On a version without custom connectors?',
  text: 'Go to Settings → Developer → Edit Config instead and let the mcp-remote adapter handle the sign-in.',
  snippet: { code: MCP_REMOTE_ENTRY, variant: 'multi' },
};

const KIRO_OAUTH_FALLBACK: McpClientFallback = {
  title: 'On a Kiro build that can sign in?',
  text: 'Skip the token entirely: put the entry below in ~/.kiro/settings/mcp.json and sign in on first tool use.',
  snippet: { code: KIRO_OAUTH_ENTRY, variant: 'multi' },
};

const MCP_CLIENTS: McpClient[] = [
  {
    id: 'claude_code',
    name: 'Claude Code',
    icon: IconClaudeColor,
    label: 'With Claude Code',
    snippet: { code: `claude mcp add --transport http bitrise ${MCP_SERVER_URL}`, variant: 'single' },
    note: 'Run in your terminal, restart Claude Code, then sign in to Bitrise on first tool use.',
  },
  {
    id: 'claude_desktop',
    name: 'Claude Desktop',
    icon: IconClaudeColor,
    label: 'With Claude Desktop',
    steps: [
      { text: 'Settings then Connectors then Add custom connector' },
      { text: 'Paste the URL below, then Add' },
      { text: 'Sign in to Bitrise when the browser opens' },
    ],
    snippet: { code: MCP_SERVER_URL, variant: 'single' },
    fallback: MCP_REMOTE_FALLBACK,
  },
  {
    id: 'vs_code',
    name: 'VS Code',
    icon: IconVsCode,
    deeplink: {
      label: 'Add to VS Code',
      href: vsCodeDeeplink,
      note: 'Opens the app and prompts Bitrise sign-in, no files to edit.',
    },
    label: 'To add manually',
    steps: [
      { text: 'Run MCP: Add Server, or edit settings.json directly' },
      { text: 'Paste the entry below, save' },
      { text: 'Sign in to Bitrise on first tool use' },
    ],
    snippet: { code: VS_CODE_ENTRY, variant: 'multi' },
    fallback: TOKEN_FALLBACK,
  },
  {
    id: 'cursor',
    name: 'Cursor',
    icon: IconCursor,
    deeplink: {
      label: 'Add to Cursor',
      href: cursorDeeplink,
      note: 'Opens the app and prompts Bitrise sign-in, no files to edit.',
    },
    label: 'To add manually',
    steps: [
      { text: 'Open ~/.cursor/mcp.json' },
      { text: 'Paste the entry below, save' },
      { text: 'Restart Cursor, then sign in on first tool use' },
    ],
    snippet: { code: REMOTE_ENTRY, variant: 'multi' },
    fallback: TOKEN_FALLBACK,
  },
  {
    id: 'copilot',
    name: 'Copilot',
    icon: IconCopilot,
    label: 'With GitHub Copilot (IDE)',
    steps: [
      { text: 'Create .mcp.json in your solution or home folder (the exact path differs by IDE)' },
      { text: 'Paste the entry below, save' },
      { text: 'Sign in to Bitrise on first tool use' },
    ],
    snippet: { code: COPILOT_ENTRY, variant: 'multi' },
    fallback: TOKEN_FALLBACK,
  },
  {
    id: 'windsurf',
    name: 'Windsurf',
    icon: IconWindsurf,
    label: 'With Windsurf',
    steps: [
      { text: 'Hammer icon in Cascade then Configure' },
      { text: 'Paste the entry below, save, click Refresh' },
      { text: 'Sign in to Bitrise on first tool call' },
    ],
    snippet: { code: WINDSURF_ENTRY, variant: 'multi' },
    fallback: TOKEN_FALLBACK,
  },
  {
    id: 'gemini_cli',
    name: 'Gemini CLI',
    icon: IconGoogleColor,
    label: 'With Gemini CLI',
    snippet: {
      code: 'gemini extensions install https://github.com/bitrise-io/bitrise-mcp',
      variant: 'single',
    },
    note: 'Run in your terminal, then sign in to Bitrise on first tool use.',
  },
  {
    id: 'kiro',
    name: 'Kiro',
    icon: IconKiroColor,
    label: 'With AWS Kiro',
    steps: [
      {
        text: 'Powers panel then Add power from GitHub, paste the repository URL below',
        snippet: {
          code: 'https://github.com/bitrise-io/bitrise-mcp/tree/main/kiro-powers/bitrise-ci',
          variant: 'single',
        },
      },
      createTokenStep,
      {
        text: 'Add the export line below to ~/.zshrc or ~/.bashrc, restart your terminal, then start Kiro',
        snippet: { code: 'export BITRISE_TOKEN="your-token-here"', variant: 'single' },
      },
    ],
    fallback: KIRO_OAUTH_FALLBACK,
  },
  {
    id: 'other',
    name: 'Other',
    icon: IconGlobe,
    label: 'Other MCP client',
    steps: [{ text: 'Add a remote server entry' }, createTokenStep, { text: 'Paste the entry below with your token' }],
    snippet: { code: REMOTE_ENTRY_WITH_TOKEN, variant: 'multi' },
  },
];

export default MCP_CLIENTS;
